import angular from 'angular'
import clone from 'lodash/clone'

import { VD_EVENTS } from '../../constants'

/**
 * Opens a modal with the provided content. `Modal` is a low level service usually used to build other modals
 * it doesn't come with any UI but only provides the base opening / closing functionality.
 *
 * Pressing `ESC` key will close an opened modal, unless the `dismissible` option is set.
 *
 * @note This service is a light wrapper around `angular-modal-service` which needs
 * to be added to any app using this service (see bower.json).
 *
 * @param {object} options The modal options. Options are accessible in the modal template as `modalCtrl.options`.
 *
 * @param {string} options.template The template of the modal. The `modalCtrl` controller is exposed to the
 * template scope.
 *
 * 2 properties are exposed to it:
 *
 * - `modalCtrl.close` a method to close the modal
 * - `modalCtrl.options` the options passed to the `Modal` service.
 *
 * The `modal` instance is set on the controller and can be used to access the `close` and `closed` promises inside the
 * controller e.g. `this.modal.closed.then(() => { // Do something once modal has completed closing here })`
 *
 * @param {boolean} options.dismissible If false, pressing ESC won't dismiss the modal. The dismissible option can also
 * be manipulated dynamically by updating the 'dismissible' controller property of the ModalService instance.
 *
 * @return The `ModalService` instance.
 *
 * @example
 * <script>
 *     function ModalDemoCtrl($scope, Modal) {
 *         $scope.open = function() {
 *            Modal({
 *               template: `<vd-panel style="position: absolute; left: 50%; top: 100px;">
 *                      This is a modal
 *                      <div>
 *                          <button class="vd-btn vd-btn--do" ng-click="modalCtrl.close()">Close modal</button>
 *                      </div>
 *                   </vd-panel>`
 *             })
 *         }
 *     }
 * </script>
 * <div ng-controller="ModalDemoCtrl">
 *     <button class="vd-btn vd-btn--do" ng-click="open()">Open modal</button>
 * </div>
 */

angular
  .module('vend.ui')
  .factory(
    'Modal',
    function ($rootScope, $log, ModalService, vdModalsContainerService) {
      return function (options) {
        const baseModalConfig = clone(options)

        // Defaulting controller for Simple and Confirm modals.
        baseModalConfig.controller = baseModalConfig.controller || 'ModalCtrl'
        baseModalConfig.controllerAs =
          baseModalConfig.controllerAs || 'modalCtrl'

        baseModalConfig.appendElement = vdModalsContainerService.getModalsContainerElement()
        baseModalConfig.showOverlay = true

        // This is just so we don't break backwards compatibility. For some reason we deviate the structure of our modal
        // config from `ModalService`, i.e. having `config.input` options directly on the `config` object.
        baseModalConfig.inputs = { options }

        return ModalService.showModal(baseModalConfig).then(
          modal => {
            let unbindOverlayListener

            /**
             * Removes and unbinds overlay click event listener if active
             *
             * @private
             * @method _removeOverlayListener
             */
            function _removeOverlayListener() {
              if (unbindOverlayListener) {
                unbindOverlayListener()
                unbindOverlayListener = null
              }
            }

            // Expose modal on the controller so the close/closed promises can be accessed by the controller
            modal.controller.modal = modal

            // Expose dismissible on the controller (if not already set) so it can be controlled dynamically.
            if (angular.isUndefined(modal.controller.dismissible)) {
              modal.controller.dismissible = options.dismissible
            }

            modal.scope.$watch(
              `${baseModalConfig.controllerAs}.dismissible`,
              dismissible => {
                _removeOverlayListener()

                if (angular.isUndefined(dismissible) || dismissible === true) {
                  unbindOverlayListener = $rootScope.$on(
                    VD_EVENTS.vdOverlayClicked,
                    () => {
                      angular.isFunction(modal.controller.close)
                        ? modal.controller.close()
                        : modal.closeModal()
                    }
                  )
                }
              }
            )

            // Remove overlay listener once the modal has been dismissed.
            // It's okay to ignore rejections here because they should be handled elsewhere.
            modal.close.catch(angular.noop).finally(() => {
              _removeOverlayListener()
            })

            return modal
          },
          error => {
            $log.error(
              '[Modal Service] Error occurred opening modal',
              error,
              options
            )
          }
        )
      }
    }
  )
